import React, { useEffect, useRef } from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';

/**
 * v2 toast — status-tinted 1px border card; no shadow (it sits on the canvas,
 * not floating above it). Presentational; stacking owned by callers.
 * `timed` is the quiet "done" after a wait moved the page on: it floats
 * (white card, soft shadow), carries no buttons, fades in, holds ~5 s and
 * fades out by itself; `onDone` fires after TIMED_TOAST_MS so the caller can
 * unmount it.
 */
export type ToastStatus = 'success' | 'warn' | 'danger' | 'neutral';

const styles: Record<ToastStatus, { box: string; Icon: React.ElementType }> = {
  success: { box: 'border-success-border bg-success-bg text-success-ink', Icon: CheckCircle2 },
  warn: { box: 'border-warn-border bg-warn-bg text-warn-ink', Icon: AlertTriangle },
  danger: { box: 'border-danger-border bg-danger-bg text-danger-ink', Icon: AlertCircle },
  neutral: { box: 'border-neutral-border bg-neutral-bg text-neutral-ink', Icon: Info },
};

const timedStyles: Record<ToastStatus, { border: string; icon: string }> = {
  success: { border: 'border-success-border', icon: 'text-success-ink' },
  warn: { border: 'border-warn-border', icon: 'text-warn-ink' },
  danger: { border: 'border-danger-border', icon: 'text-danger-ink' },
  neutral: { border: 'border-line', icon: 'text-neutral-ink' },
};

/** Matches the toast-life animation, so reduced motion still clears it. */
export const TIMED_TOAST_MS = 6000;

export interface ToastProps {
  status?: ToastStatus;
  title: React.ReactNode;
  message?: React.ReactNode;
  onDismiss?: () => void;
  /** Auto-fading variant (no dismiss button); `onDone` fires when it has faded. */
  timed?: boolean;
  onDone?: () => void;
  className?: string;
}

export const Toast: React.FC<ToastProps> = ({
  status = 'neutral',
  title,
  message,
  onDismiss,
  timed = false,
  onDone,
  className = '',
}) => {
  const { box, Icon } = styles[status];
  // A ref, so a parent re-render with a fresh callback doesn't restart the clock.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    if (!timed) return undefined;
    const timer = window.setTimeout(() => onDoneRef.current?.(), TIMED_TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [timed]);
  if (timed) {
    return (
      <div
        role="status"
        data-timed=""
        className={`flex w-80 items-start gap-s4 rounded-card border ${timedStyles[status].border} bg-surface px-s5 py-[11px] text-[12.5px] text-ink shadow-floating motion-safe:animate-toast-life ${className}`}
      >
        <Icon aria-hidden="true" className={`mt-[1px] h-[16px] w-[16px] shrink-0 ${timedStyles[status].icon}`} />
        <div className="flex-1">
          <p className="font-semibold">{title}</p>
          {message && <p className="mt-[1px] text-meta text-ink-secondary">{message}</p>}
        </div>
      </div>
    );
  }
  return (
    <div role="status" className={`flex w-80 items-start gap-s3 rounded-card border px-s4 py-s3 ${box} ${className}`}>
      <Icon className="mt-[1px] h-[15px] w-[15px] shrink-0" />
      <div className="flex-1">
        <p className="text-row-title">{title}</p>
        {message && <p className="mt-s1 text-body-sm opacity-90">{message}</p>}
      </div>
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="rounded-pill p-s1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
        >
          <X className="h-[13px] w-[13px]" />
        </button>
      )}
    </div>
  );
};

export default Toast;
